import React, { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/auth";
import { useLang } from "@/i18n";
import { LangToggle } from "@/components/LangToggle";
import { toast } from "sonner";
import { Pulse } from "@phosphor-icons/react";

export default function Login() {
  const { login } = useAuth();
  const { t } = useLang();
  const [loginId, setLoginId] = useState("");
  const [pin, setPin] = useState("");
  const [remember, setRemember] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showResetInfo, setShowResetInfo] = useState(false);

  const submit = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { login_id: loginId, pin });
      login(data.token, data.user);
      toast.success(t("welcome") + ", " + data.user.name);
    } catch (e) {
      if (e.response?.status === 429) toast.error(t("too_many_attempts"));
      else toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-slate-900 flex flex-col">
      <div className="flex justify-end p-4"><LangToggle /></div>
      <div className="flex-1 flex flex-col justify-center px-6 max-w-md mx-auto w-full">
        <div className="flex items-center gap-3 mb-2">
          <div className="h-12 w-12 rounded-xl bg-emerald-500 grid place-items-center">
            <Pulse size={26} weight="bold" className="text-white" />
          </div>
          <div>
            <h1 className="font-head font-extrabold text-2xl text-white leading-tight">{t("app_name")}</h1>
            <p className="text-slate-400 text-sm">{t("company")}</p>
          </div>
        </div>

        <div className="mt-8 rounded-2xl bg-white p-6 space-y-4">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">{t("employee_id")}</label>
            <input
              data-testid="login-id-input"
              value={loginId} onChange={(e) => setLoginId(e.target.value)}
              className="mt-1 w-full min-h-[52px] rounded-lg border border-slate-200 px-4 text-lg"
              placeholder=""
            />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              {t("pin")}
            </label>
            <input
              data-testid="login-pin-input"
              value={pin}
              onChange={(e) => {
                const v = e.target.value.replace(/\D/g, "").slice(0, 4);
                setPin(v);
              }}
              inputMode="numeric" type="password"
              className="mt-1 w-full min-h-[52px] rounded-lg border border-slate-200 px-4 text-2xl tracking-[0.5em] font-bold"
              placeholder="••••"
            />
          </div>

          <div className="flex items-center justify-between text-sm">
            <label className="flex items-center gap-2 text-slate-600">
              <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} data-testid="remember-me" className="h-4 w-4" />
              {t("remember_me")}
            </label>
            <button onClick={() => setShowResetInfo(true)} className="text-emerald-600 font-semibold" data-testid="forgot-pin">{t("forgot_pin")}</button>
          </div>

          {showResetInfo && (
            <div data-testid="reset-info-message" className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800">
              {t("contact_admin_reset")}
            </div>
          )}

          <button
            onClick={submit}
            disabled={loading || !loginId || pin.length < 4}
            data-testid="login-submit"
            className="w-full min-h-[52px] rounded-lg bg-emerald-600 text-white font-bold text-lg disabled:opacity-40 active:scale-[0.98]"
          >
            {loading ? "…" : t("login")}
          </button>
        </div>
      </div>
    </div>
  );
}
